'use client';

import {
  DatePicker,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
} from '@workspace/ui';
import {
  camelToSnakeCase,
  type DateConstraintContext,
  type DateResolvableValue,
  DYNAMIC_FIELD_TYPES,
  dynamicFieldCalculate,
  formatReadOnlyDateValue,
  getDefaultSelectedMonth,
  getDisabledDate,
  isSameDay,
  resolveFieldConstraints,
  shouldShowField,
} from '@workspace/ui/lib';
import type { CountryField } from '@workspace/ui/types';
import { useEffect, useMemo } from 'react';
import type { UseFormReturn } from 'react-hook-form';

type DynamicFieldValue = string | Date | string[] | null | undefined;

const hasFieldValue = (type: string, rawValue: DynamicFieldValue) => {
  switch (type) {
    case DYNAMIC_FIELD_TYPES.DATE:
      if (rawValue instanceof Date) return !Number.isNaN(rawValue.getTime());
      if (typeof rawValue === 'string') return rawValue.trim() !== '';
      return false;
    case DYNAMIC_FIELD_TYPES.SELECT:
      return Array.isArray(rawValue)
        ? rawValue.length > 0
        : typeof rawValue === 'string' && rawValue.trim() !== '';
    default:
      if (typeof rawValue === 'string') return rawValue.trim() !== '';
      if (rawValue instanceof Date) return !Number.isNaN(rawValue.getTime());
      if (Array.isArray(rawValue)) return rawValue.length > 0;
      return false;
  }
};

// ---------- DynamicDateField ----------

const DynamicDateField = ({
  countryField,
  form,
  readOnly,
  resolveRefDate,
  placeholder,
  skipNamedRulesForFields,
}: {
  countryField: CountryField;
  form: UseFormReturn;
  readOnly: boolean;
  resolveRefDate: (value: DateResolvableValue) => Date | undefined;
  placeholder?: string;
  skipNamedRulesForFields?: string[];
}) => {
  const {
    name,
    label,
    min_months,
    max_months,
    start_date_ref,
    conditional_depends,
    calculation,
    override = false,
  } = countryField;

  const startRefValue = start_date_ref ? resolveRefDate(form.watch(start_date_ref)) : undefined;

  const conditionalFields = conditional_depends?.fields ?? [];
  const conditionalFromField = conditionalFields[0] ? form.watch(conditionalFields[0]) : undefined;
  const conditionalToField = conditionalFields[1] ? form.watch(conditionalFields[1]) : undefined;

  const { dependantMaxDate } = resolveFieldConstraints(
    countryField,
    [conditionalFromField, conditionalToField],
    resolveRefDate
  );

  const calculatedDate =
    startRefValue && calculation ? dynamicFieldCalculate(startRefValue, calculation) : undefined;

  const constraintContext: DateConstraintContext = useMemo(
    () => ({
      startDate: startRefValue,
      minMonths: min_months,
      maxMonths: max_months,
      dependantMaxDate,
    }),
    [startRefValue, dependantMaxDate, min_months, max_months]
  );

  // eor-app naming support - unfortunate case normalisation, but unavoidable for overrides.
  // TODO: remove this once eor-app is updated to use snake_case field names
  const normalizedSnakeCaseName = override ? camelToSnakeCase(name) : name;

  const defaultMonth = useMemo(
    () => getDefaultSelectedMonth(normalizedSnakeCaseName, constraintContext),
    [normalizedSnakeCaseName, constraintContext]
  );
  const disabledDates = useMemo(
    () =>
      getDisabledDate(normalizedSnakeCaseName, constraintContext, {
        skipNamedRulesForFields,
      }),
    [normalizedSnakeCaseName, constraintContext, skipNamedRulesForFields]
  );

  // Disable the picker entirely when a required date reference is missing.
  const isFieldDisabled =
    calculatedDate !== undefined || (start_date_ref !== undefined && startRefValue === undefined);

  // Clear the current value whenever constraints change and it falls outside the valid range.
  useEffect(() => {
    if (readOnly || !disabledDates) return;

    const currentValue = form.getValues(name);

    if (!(currentValue instanceof Date) || Number.isNaN(currentValue.getTime())) return;

    if (disabledDates(currentValue)) {
      form.setValue(name, undefined);
    }
  }, [disabledDates, name, form, readOnly]);

  // prefill date field if calculation option is present
  useEffect(() => {
    if (!calculatedDate) return;

    const currentValue = resolveRefDate(form.getValues(name));
    if (currentValue && isSameDay(currentValue, calculatedDate)) return;

    form.setValue(name, calculatedDate);
  }, [calculatedDate, form, name, resolveRefDate]);

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            {readOnly ? (
              <Input
                value={formatReadOnlyDateValue(field.value)}
                placeholder={placeholder}
                readOnly
                parentClassName="w-full"
              />
            ) : (
              <DatePicker
                {...field}
                onBlur={field.onBlur}
                placeholder={placeholder}
                disabled={isFieldDisabled}
                defaultMonth={defaultMonth}
                disabledDates={disabledDates}
              />
            )}
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

// ---------- DynamicSelectField ----------

const DynamicSelectField = ({
  countryField,
  form,
  readOnly,
  resolveRefDate,
  placeholder,
}: {
  countryField: CountryField;
  form: UseFormReturn;
  readOnly: boolean;
  resolveRefDate: (value: DateResolvableValue) => Date | undefined;
  placeholder?: string;
}) => {
  const conditionalFields = countryField.conditional_depends?.fields ?? [];
  const conditionalFromField = conditionalFields[0] ? form.watch(conditionalFields[0]) : undefined;
  const conditionalToField = conditionalFields[1] ? form.watch(conditionalFields[1]) : undefined;

  const allOptions = useMemo(
    () =>
      Object.entries(countryField.options ?? {}).map(([key, value]) => ({
        name: String(value),
        value: key,
      })),
    [countryField.options]
  );

  const { allowedOptionKeys } = resolveFieldConstraints(
    countryField,
    [conditionalFromField, conditionalToField],
    resolveRefDate
  );

  const options = useMemo(() => {
    if (!allowedOptionKeys) return allOptions;
    const allowedSet = new Set(allowedOptionKeys);
    return allOptions.filter((opt) => allowedSet.has(opt.value));
  }, [allOptions, allowedOptionKeys]);

  const currentValue = form.watch(countryField.name) as string | undefined;

  useEffect(() => {
    const defaultValue = countryField.default_value;
    if (defaultValue === undefined || defaultValue === null) return;
    if (typeof currentValue === 'string' && currentValue.trim() !== '') return;

    form.setValue(countryField.name, String(defaultValue));
  }, [countryField.default_value, countryField.name, currentValue, form]);

  useEffect(() => {
    if (readOnly) return;
    if (!currentValue) return;
    if (!options.some((opt) => opt.value === currentValue)) {
      form.setValue(countryField.name, '');
    }
  }, [options, currentValue, form, countryField.name, readOnly]);

  const selectedOptionName = options.find((option) => option.value === currentValue)?.name;

  return (
    <FormField
      control={form.control}
      name={countryField.name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{countryField.label}</FormLabel>
          <FormControl>
            {readOnly ? (
              <Input
                value={selectedOptionName ?? ''}
                placeholder={placeholder}
                readOnly
                parentClassName="w-full"
              />
            ) : (
              <Select
                options={options}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                placeholder={placeholder}
              />
            )}
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

// ---------- DynamicTextField ----------

const DynamicTextField = ({
  countryField,
  form,
  readOnly,
  placeholder,
}: {
  countryField: CountryField;
  form: UseFormReturn;
  readOnly: boolean;
  placeholder?: string;
}) => (
  <FormField
    control={form.control}
    name={countryField.name}
    render={({ field }) => (
      <FormItem>
        <FormLabel>{countryField.label}</FormLabel>
        <FormControl>
          {readOnly ? (
            <Input
              value={field.value as string}
              placeholder={placeholder}
              readOnly
              parentClassName="w-full"
            />
          ) : (
            <Input
              {...field}
              value={field.value as string}
              placeholder={placeholder}
              maxLength={countryField.max_length}
            />
          )}
        </FormControl>
        <FormMessage />
      </FormItem>
    )}
  />
);

// ---------- DynamicCountryFields ----------

/**
 * Renders a list of dynamic country-specific form fields.
 *
 * @param resolveRefDate - Optional converter from a watched form value to a `Date`.
 *   Defaults to a direct cast (`value as Date`). Override this when the form stores
 *   date-like reference fields (e.g. `start_date`) as formatted strings rather than
 *   `Date` objects, which is the case for the admin employment form.
 */
const DynamicCountryFields = ({
  countryFieldsForCountry,
  form,
  readOnly = false,
  resolveRefDate,
  placeholder,
  skipNamedRulesForFields,
}: {
  countryFieldsForCountry: CountryField[];
  form: UseFormReturn;
  readOnly?: boolean;
  resolveRefDate?: (value: DateResolvableValue) => Date | undefined;
  placeholder?: string;
  skipNamedRulesForFields?: string[];
}) => {
  const resolveDate =
    resolveRefDate ?? ((value: DateResolvableValue) => (value instanceof Date ? value : undefined));

  return (
    <>
      {countryFieldsForCountry?.length > 0 &&
        countryFieldsForCountry.map((countryField) => {
          const isFieldReadOnly = readOnly || !!countryField.read_only;

          if (!shouldShowField(countryField, form)) return null;

          if (countryField.type === DYNAMIC_FIELD_TYPES.DATE) {
            return (
              <DynamicDateField
                key={countryField.name}
                countryField={countryField}
                form={form}
                readOnly={isFieldReadOnly}
                resolveRefDate={resolveDate}
                placeholder={placeholder}
                skipNamedRulesForFields={skipNamedRulesForFields}
              />
            );
          }

          if (
            countryField.type === DYNAMIC_FIELD_TYPES.SELECT &&
            (countryField.options || placeholder)
          ) {
            return (
              <DynamicSelectField
                key={countryField.name}
                countryField={countryField}
                form={form}
                readOnly={isFieldReadOnly}
                resolveRefDate={resolveDate}
                placeholder={placeholder}
              />
            );
          }

          return (
            <DynamicTextField
              key={countryField.name}
              countryField={countryField}
              form={form}
              readOnly={isFieldReadOnly}
              placeholder={placeholder}
            />
          );
        })}
    </>
  );
};

export { DynamicCountryFields, hasFieldValue };
